/**
 * Structural guard for the `status` SSE frame (Genesis-Live bridge, L6b) —
 * the browser's defense-in-depth check before trusting the freshness badge,
 * mirroring `validateRoster`'s role for `roster` frames (see
 * live-validate.ts). Only the fields the store keeps are extracted; the
 * rest of the frame (`reason`, `tick`, `seq`, `chainVerified`,
 * `cognitionHealth`) belongs to the richer evidence panel a later phase
 * renders.
 *
 * `mode` IS kept: an engineering canary is not the flagship universe, and a
 * surface that cannot tell the two apart ends up printing plain LIVE over a
 * canary population (finding M3). Anything other than the two known modes
 * reads as null — unknown, never assumed to be the live universe.
 *
 * Pure module: no Next/React/store imports — importable directly under
 * `node --test --experimental-strip-types`.
 */

import type { Freshness, FreshnessReason, LiveMode } from './types.ts';

const FRESHNESS_VALUES: readonly Freshness[] = ['LIVE', 'STALE', 'OFFLINE', 'SYNCING'];
const REASON_VALUES: readonly FreshnessReason[] = [
  'no progress',
  'provider outage',
  'epoch boundary',
  'code fallback',
  'unconfigured',
  'unreachable',
  'observer error',
  'no run',
  'projection error',
];
const MODE_VALUES: readonly LiveMode[] = ['live', 'canary'];
const RUN_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;

/**
 * Population counters carried by the frame: how many agents the observed
 * universe holds, and how many rows the roster frame beside it carries. A
 * surface that reports a population size must read `activeAgents`; the roster
 * array's own length only ever describes what was sent.
 */
export interface LiveStatusStats {
  activeAgents: number;
  rosterShown: number;
}

export interface LiveStatusFrame {
  freshness: Freshness;
  /** Why the state is not LIVE, when the bridge said so. Unknown values drop to undefined. */
  reason?: FreshnessReason;
  runId: string | null;
  epoch: number | null;
  observedAt: string | null;
  /** `live` = the flagship Genesis-Live universe, `canary` = an engineering run. */
  mode: LiveMode | null;
  /** Null when the frame carried no counters (an OFFLINE frame has none). */
  stats: LiveStatusStats | null;
}

/** Counters are dropped, never fatal: a frame without them still carries freshness. */
function readStats(value: unknown): LiveStatusStats | null {
  if (typeof value !== 'object' || value === null) return null;
  const o = value as Record<string, unknown>;
  const activeAgents = o.activeAgents;
  const rosterShown = o.rosterShown;
  if (!Number.isSafeInteger(activeAgents) || (activeAgents as number) < 0) return null;
  if (!Number.isSafeInteger(rosterShown) || (rosterShown as number) < 0) return null;
  return { activeAgents: activeAgents as number, rosterShown: rosterShown as number };
}

/** Returns null on any structural deviation — callers keep the previous status. */
export function parseStatusFrame(value: unknown): LiveStatusFrame | null {
  if (typeof value !== 'object' || value === null) return null;
  const o = value as Record<string, unknown>;

  const freshness = o.freshness;
  if (typeof freshness !== 'string' || !FRESHNESS_VALUES.includes(freshness as Freshness)) return null;

  const runId = o.runId;
  if (runId !== null && runId !== undefined && (typeof runId !== 'string' || !RUN_ID_RE.test(runId))) {
    return null;
  }

  const epoch = o.epoch;
  if (
    epoch !== null &&
    epoch !== undefined &&
    (typeof epoch !== 'number' || !Number.isSafeInteger(epoch) || epoch < 0)
  ) {
    return null;
  }

  const observedAt = o.observedAt;
  if (
    observedAt !== null &&
    observedAt !== undefined &&
    (typeof observedAt !== 'string' || !ISO_RE.test(observedAt))
  ) {
    return null;
  }

  const mode = o.mode;
  if (mode !== null && mode !== undefined && (typeof mode !== 'string' || !MODE_VALUES.includes(mode as LiveMode))) {
    return null;
  }

  const reason = o.reason;
  const knownReason =
    typeof reason === 'string' && REASON_VALUES.includes(reason as FreshnessReason)
      ? (reason as FreshnessReason)
      : undefined;

  return {
    freshness: freshness as Freshness,
    ...(knownReason === undefined ? {} : { reason: knownReason }),
    runId: typeof runId === 'string' ? runId : null,
    epoch: Number.isSafeInteger(epoch) ? (epoch as number) : null,
    observedAt: typeof observedAt === 'string' ? observedAt : null,
    mode: typeof mode === 'string' ? (mode as LiveMode) : null,
    stats: readStats(o.stats),
  };
}
